import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Clock,
  Eye,
  Flame,
  Loader2,
  Send,
  Sparkles,
  Star,
  Swords,
  Target,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { useAssignedCounts } from '@/hooks/useDiscoveryQueue';
import { useLeaderboard, useChallenges, useStreaks, useWeeklyMinimum } from '@/hooks/useGamification';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { RetainerCard } from '@/components/RetainerCard';
import { WeeklyMinimumCard } from '@/components/WeeklyMinimumCard';
import { formatMoney } from '@/lib/money';
import { formatDate } from '@/lib/dates';
import { creatorTypeLabel, payModelLabel, rateTypeLabel, teamLabel, titleCase } from '@/lib/labels';
import type {
  ChallengeMetric,
  ChallengeWithStandings,
  LeaderboardEntry,
  MeTeam,
  OutreachInvite,
  OutreachNiche,
  OutreachRate,
} from '@/api/types';

// ── Pure helpers (exported for tests) ──────────────────────────────────────

/** The stages the payout calculator actually pays, in flow order. */
export const PAID_STAGES = ['LOG_BONUS', 'JOIN_BONUS', 'POST_BASE'] as const;
export type PaidStage = (typeof PAID_STAGES)[number];

export interface StagePay {
  rateType: PaidStage;
  /** Base amount for every invite of this type. Null when no base row exists. */
  amount: number | null;
  /**
   * The largest niche-scoped amount for this stage, added on top of the base
   * when the invite's first niche matches. Never summed across niches: an
   * invite only ever earns one niche's bonus.
   */
  maxNicheBonus: number | null;
}

export interface CreatorTypePay {
  category: string;
  stages: StagePay[];
}

function toAmount(v: string): number | null {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * What each creator type pays per stage, from the /portal/rates list.
 *
 * Only LOG_BONUS / JOIN_BONUS / POST_BASE rows count; NICHE_BONUS and
 * INFLUENCER_FLAT rows exist in the rate table but the backend never pays
 * them. `categories` (the team's allowed creator types) sets the order;
 * types with rates but missing from it are appended. Stages with no rows
 * are omitted, and so are types with no stages.
 */
export function payByCreatorType(
  rates: ReadonlyArray<OutreachRate>,
  categories: ReadonlyArray<string> = [],
): CreatorTypePay[] {
  const byCat = new Map<string, Map<PaidStage, StagePay>>();
  for (const r of rates) {
    if (!r.isActive) continue;
    if (!(PAID_STAGES as ReadonlyArray<string>).includes(r.rateType)) continue;
    const stage = r.rateType as PaidStage;
    const amount = toAmount(r.amount);
    if (amount === null) continue;
    const stages = byCat.get(r.category) ?? new Map<PaidStage, StagePay>();
    const entry = stages.get(stage) ?? { rateType: stage, amount: null, maxNicheBonus: null };
    if (r.niche === null) {
      entry.amount = amount;
    } else if (amount > 0 && (entry.maxNicheBonus === null || amount > entry.maxNicheBonus)) {
      entry.maxNicheBonus = amount;
    }
    stages.set(stage, entry);
    byCat.set(r.category, stages);
  }

  const order = [...categories, ...Array.from(byCat.keys()).filter((c) => !categories.includes(c))];
  const out: CreatorTypePay[] = [];
  for (const category of order) {
    const stages = byCat.get(category);
    if (!stages) continue;
    const list = PAID_STAGES.map((t) => stages.get(t)).filter((s): s is StagePay => !!s);
    if (list.length > 0) out.push({ category, stages: list });
  }
  return out;
}

/**
 * The stage lines worth showing: a $0.00 line reads as "you earn nothing
 * here" and only adds noise. A $0 base with a niche bonus stays, since the
 * bonus is still on offer. Types left with no lines are dropped.
 */
export function withoutZeroStages(types: ReadonlyArray<CreatorTypePay>): CreatorTypePay[] {
  return types
    .map((t) => ({
      ...t,
      stages: t.stages.filter((s) => (s.amount ?? 0) > 0 || s.maxNicheBonus !== null),
    }))
    .filter((t) => t.stages.length > 0);
}

/** Niche codes that carry a paid niche-scoped rate, for the "bonus niches" chips. */
export function bonusNicheCodes(rates: ReadonlyArray<OutreachRate>): string[] {
  const codes = new Set<string>();
  for (const r of rates) {
    if (!r.isActive || r.niche === null) continue;
    if (!(PAID_STAGES as ReadonlyArray<string>).includes(r.rateType)) continue;
    const amount = toAmount(r.amount);
    if (amount !== null && amount > 0) codes.add(r.niche);
  }
  return Array.from(codes);
}

/** What counts toward each challenge metric, in plain words. */
export const CHALLENGE_COUNTS: Record<ChallengeMetric, string> = {
  MOST_INVITES: 'Invites you log',
  MOST_CONVERSIONS: 'Creators you logged who post',
  HIGHEST_CONVERSION_RATE: 'Share of your logged creators who post',
  MOST_POSTS: 'Approved posts by creators you logged',
  MOST_VIEWS: 'Views on posts by creators you logged',
};

/** Unit shown next to a challenge score. */
const CHALLENGE_UNIT: Record<ChallengeMetric, string> = {
  MOST_INVITES: 'invites',
  MOST_CONVERSIONS: 'posted',
  HIGHEST_CONVERSION_RATE: '',
  MOST_POSTS: 'posts',
  MOST_VIEWS: 'views',
};

export function challengeCounts(metric: string): string {
  return CHALLENGE_COUNTS[metric as ChallengeMetric] ?? '';
}

/**
 * A challenge's prize is only on offer to the team it targets. Null target
 * means every team; an unknown own team (null) only sees open challenges.
 */
export function prizeAppliesToTeam(targetTeam: string | null, myTeamCode: string | null): boolean {
  if (targetTeam === null) return true;
  return myTeamCode !== null && targetTeam === myTeamCode;
}

/** "1 Oct 2026 – 14 Oct 2026" */
export function challengeDates(startDate: string, endDate: string): string {
  return `${formatDate(startDate)} – ${formatDate(endDate)}`;
}

// ── Page ───────────────────────────────────────────────────────────────────

type LeaderboardTab = 'invites' | 'posted';

const LEADERBOARD_TABS: ReadonlyArray<{ key: LeaderboardTab; label: string; unit: string }> = [
  { key: 'invites', label: 'Invites logged', unit: 'logged' },
  { key: 'posted', label: 'Creators posted', unit: 'posted' },
];

export function DashboardPage(): JSX.Element {
  const api = useApi();
  const meQuery = useMe();
  const meData = meQuery.data && meQuery.data.role === 'outreacher' ? meQuery.data : null;
  const inviter = meData?.inviter ?? null;

  const assignedCountsQuery = useAssignedCounts();
  const directCount = assignedCountsQuery.data?.direct ?? 0;
  const teamCount = assignedCountsQuery.data?.team ?? 0;
  const totalAssigned = directCount + teamCount;

  const query = useQuery({
    enabled: !!inviter,
    queryKey: ['dashboard'],
    queryFn: async () => {
      const [invites, niches, rates] = await Promise.all([
        api.get<OutreachInvite[]>('/portal/invites', { limit: 5 }),
        // Hidden niches too, so recent invites keep their names; they're
        // filtered out of the "look for" lists below.
        api.get<OutreachNiche[]>('/portal/niches', { includeHidden: true }),
        api.get<OutreachRate[]>('/portal/rates'),
      ]);
      return { invites, niches, rates };
    },
  });

  const leaderboardQuery = useLeaderboard();
  const challengesQuery = useChallenges();
  const streaksQuery = useStreaks();
  const weeklyMinimumQuery = useWeeklyMinimum();
  const [lbTab, setLbTab] = useState<LeaderboardTab>('invites');

  if (!meData || !inviter || query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading...
      </div>
    );
  }
  if (query.error) {
    return <p className="text-sm text-destructive">{(query.error as Error).message}</p>;
  }

  const me = inviter;
  const team: MeTeam | null = meData.team;
  const teamCode = team?.code ?? me.team ?? null;
  const teamName = teamLabel(teamCode, team?.displayName);
  const teamTracks = meData.teamSettings.clippingResolveRequired;
  const { invites, niches, rates } = query.data!;
  const nicheByCode = new Map(niches.map((n) => [n.code, n]));
  const nicheName = (code: string): string => nicheByCode.get(code)?.displayName ?? titleCase(code);

  const payTypes = withoutZeroStages(payByCreatorType(rates, team?.allowedCategories ?? []));
  const bonusNiches = bonusNicheCodes(rates)
    .filter((code) => !nicheByCode.get(code)?.isHidden)
    .map((code) => ({ code, name: nicheName(code), isFocused: nicheByCode.get(code)?.isFocused ?? false }))
    .sort((a, b) => Number(b.isFocused) - Number(a.isFocused) || a.name.localeCompare(b.name));
  const focusOnly = niches.filter(
    (n) => n.isFocused && !n.isHidden && !bonusNiches.some((b) => b.code === n.code),
  );
  const myStreak = streaksQuery.data?.myStreak ?? 0;
  const challenges = challengesQuery.data ?? [];
  const isRetainer = me.compensationModel === 'RETAINER';
  const payLine =
    isRetainer && me.retainerAmount
      ? `${payModelLabel(me.compensationModel)}: $${formatMoney(me.retainerAmount)}`
      : payModelLabel(me.compensationModel);
  const activeTab = LEADERBOARD_TABS.find((t) => t.key === lbTab) ?? LEADERBOARD_TABS[0]!;

  return (
    <div className="space-y-10">
      {/* ── Hero ────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-brand/[0.08] via-brand/[0.03] to-transparent px-6 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">Hey, {me.name.split(' ')[0]}</h1>
              {myStreak >= 2 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-1 text-sm font-bold tabular-nums text-orange-400 ring-1 ring-orange-500/20">
                  🔥 {myStreak} day streak
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted/80 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  🔥 {myStreak === 1 ? 'Keep it going tomorrow!' : 'Start your streak today!'}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {teamName ? `${teamName} team · ` : ''}
              {payLine}
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 gap-2 shadow-md">
            <Link to="/portal/log">
              <Zap className="h-4 w-4" /> Log reply
            </Link>
          </Button>
        </div>
      </div>

      {/* ── Weekly minimum (only when a manager has set one) ────── */}
      {weeklyMinimumQuery.data ? <WeeklyMinimumCard data={weeklyMinimumQuery.data} /> : null}

      {/* ── Lead queue ──────────────────────────────────────────── */}
      {totalAssigned > 0 && (
        <div className="flex items-center gap-4 rounded-xl border border-brand/30 bg-brand/[0.05] px-5 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/15">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">
              {totalAssigned} creator{totalAssigned === 1 ? '' : 's'} in your lead queue
            </p>
            <p className="text-xs text-muted-foreground">
              Picked for you or your team to message. Start with these.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/discovery-queue">
              Open lead queue <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      )}

      {/* ── What you earn ───────────────────────────────────────── */}
      {isRetainer ? (
        <section>
          <h2 className="mb-4 text-lg font-semibold">How you're paid</h2>
          <RetainerCard retainerAmount={me.retainerAmount} />
        </section>
      ) : (
      <section>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">What each invite earns</h2>
            <p className="text-sm text-muted-foreground">
              Per creator you log, credited as they reach each stage.
            </p>
          </div>
          <Button asChild variant="ghost" size="sm" className="shrink-0 text-xs text-muted-foreground">
            <Link to="/portal/rates">
              Rate card <ArrowRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>

        {payTypes.length === 0 ? (
          <Card>
            <CardContent className="flex items-start gap-3 p-5">
              <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground/50" />
              <div>
                <p className="text-sm font-medium">No rates set for your team yet</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Ask your campaign manager, or{' '}
                  <Link to="/portal/rates" className="text-primary hover:underline">check the rate card</Link>
                  {' '}later.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {payTypes.map((t) => (
                <Card key={t.category}>
                  <CardContent className="space-y-2 p-4">
                    <p className="text-sm font-semibold">{creatorTypeLabel(t.category)}</p>
                    <ul className="space-y-1.5">
                      {t.stages.map((s) => (
                        <li key={s.rateType} className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="text-muted-foreground">{rateTypeLabel(s.rateType)}</span>
                          <span className="text-right">
                            <span className="font-semibold tabular-nums">
                              ${formatMoney(s.amount ?? 0)}
                            </span>
                            {s.maxNicheBonus !== null && (
                              <span className="block text-[11px] text-muted-foreground">
                                up to +${formatMoney(s.maxNicheBonus)} niche bonus
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {bonusNiches.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold">Niches with a bonus</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Tag one of these as the first niche on an invite to add its bonus. Amounts are on the rate card.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {bonusNiches.map((n) => (
                      <span
                        key={n.code}
                        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs font-medium"
                      >
                        {n.isFocused && <Star className="h-3 w-3 fill-pending-strong text-pending-strong" />}
                        {n.name}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {focusOnly.length > 0 && (
              <Card className="border-pending-strong/20 bg-gradient-to-r from-pending-strong/[0.04] to-transparent">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-pending-strong">
                    <Star className="h-4 w-4 fill-pending-strong" />
                    Focus niches right now
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    No bonus on these, but they're the niches the team is prioritising.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {focusOnly.map((n) => (
                      <span
                        key={n.code}
                        className="rounded-full border border-pending-strong/20 bg-pending-strong/10 px-2.5 py-1 text-xs font-medium"
                      >
                        {n.displayName}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </section>
      )}

      {/* ── Challenges ──────────────────────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-lg font-semibold">Challenges</h2>
          {challenges.length > 0 && (
            <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-brand">
              {challenges.length}
            </span>
          )}
        </div>
        <p className="-mt-3 mb-4 text-sm text-muted-foreground">
          Time-boxed competitions. The top spot when a challenge ends can win its prize.
        </p>

        {challengesQuery.isLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading challenges...
            </CardContent>
          </Card>
        ) : challengesQuery.isError ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Swords className="mx-auto h-7 w-7 text-destructive/40" />
              <p className="mt-2 text-sm text-destructive">Couldn't load challenges</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {(challengesQuery.error as Error)?.message}
              </p>
            </CardContent>
          </Card>
        ) : challenges.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Swords className="mx-auto h-7 w-7 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">No active challenges right now</p>
              <p className="mt-0.5 text-xs text-muted-foreground/60">
                Check back soon. An admin can start one at any time.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {challenges.map((cws) => (
              <ChallengeCard
                key={cws.challenge.id}
                data={cws}
                myInviterId={me.id}
                myTeamCode={teamCode}
                myTeamName={team?.displayName ?? null}
                nicheName={nicheName}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Leaderboard + Recent invites ────────────────────────── */}
      <div className="grid gap-8 lg:grid-cols-5">
        {/* Leaderboard */}
        <section className="lg:col-span-3">
          <h2 className="text-lg font-semibold">Leaderboard</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Top 5 on your team, all time.
          </p>
          <Card className="overflow-hidden">
            <div className="flex gap-0.5 border-b border-border/50 bg-muted/30 px-4 py-2">
              {LEADERBOARD_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                    lbTab === tab.key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setLbTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {leaderboardQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : (
              <LeaderboardList
                entries={
                  lbTab === 'invites'
                    ? (leaderboardQuery.data?.byInvites ?? [])
                    : (leaderboardQuery.data?.byConversions ?? [])
                }
                unit={activeTab.unit}
                myInviterId={me.id}
              />
            )}
          </Card>
        </section>

        {/* Recent invites */}
        <section className="lg:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">Recent invites</h2>
              <p className="mb-4 text-sm text-muted-foreground">The last creators you logged.</p>
            </div>
            <Button asChild variant="ghost" size="sm" className="shrink-0 text-xs text-muted-foreground">
              <Link to="/portal/invites">
                My creators <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </div>
          <Card>
            <CardContent className="p-3">
              {invites.length === 0 ? (
                <div className="py-8 text-center">
                  <Target className="mx-auto h-7 w-7 text-muted-foreground/30" />
                  <p className="mt-2 text-sm text-muted-foreground">No invites yet</p>
                  <p className="mt-0.5 text-xs text-muted-foreground/60">
                    When a creator you messaged replies asking how to join, use Log reply.
                  </p>
                </div>
              ) : (
                <ul className="space-y-0.5">
                  {invites.map((inv) => (
                    <li
                      key={inv.id}
                      className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/40"
                    >
                      <span className="min-w-0 truncate text-sm">
                        <span className="text-muted-foreground/50">@</span>
                        <span className="font-medium">{inv.targetIdentifier}</span>
                      </span>
                      <InviteStagePill invite={inv} teamTracks={teamTracks} />
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}

// -- Leaderboard ----------------------------------------------------------

const MEDAL = ['🥇', '🥈', '🥉'] as const;

function LeaderboardList({
  entries,
  unit,
  myInviterId,
}: {
  entries: LeaderboardEntry[];
  unit: string;
  myInviterId: number;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <div className="py-10 text-center">
        <Trophy className="mx-auto h-7 w-7 text-muted-foreground/30" />
        <p className="mt-2 text-sm text-muted-foreground">Nobody on the board yet</p>
      </div>
    );
  }

  return (
    <div>
      {entries.map((e, i) => {
        const isMe = e.inviterId === myInviterId;
        return (
          <div
            key={e.inviterId}
            className={`flex items-center gap-3 border-b border-border/40 px-5 py-3 last:border-0 ${
              isMe ? 'bg-brand/[0.06]' : i % 2 === 0 ? '' : 'bg-muted/20'
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center text-base">
              {i < 3 ? MEDAL[i] : <span className="text-xs font-bold text-muted-foreground">#{i + 1}</span>}
            </span>
            <span className={`flex-1 truncate text-sm ${isMe ? 'font-semibold' : ''}`}>
              {e.inviterName}
              {isMe && (
                <span className="ml-1.5 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                  you
                </span>
              )}
            </span>
            <span className="tabular-nums text-sm font-semibold">{e.value}</span>
            <span className="w-16 text-right text-[11px] text-muted-foreground">{unit}</span>
          </div>
        );
      })}
    </div>
  );
}

// -- Challenge card -------------------------------------------------------

const METRIC_ICON: Record<string, typeof Trophy> = {
  MOST_INVITES: Send,
  MOST_CONVERSIONS: TrendingUp,
  HIGHEST_CONVERSION_RATE: BarChart3,
  MOST_POSTS: Flame,
  MOST_VIEWS: Eye,
};

function ChallengeCard({
  data,
  myInviterId,
  myTeamCode,
  myTeamName,
  nicheName,
}: {
  data: ChallengeWithStandings;
  myInviterId: number;
  myTeamCode: string | null;
  myTeamName: string | null;
  nicheName: (code: string) => string;
}): JSX.Element {
  const { challenge, standings } = data;
  const now = new Date();
  const startDate = new Date(challenge.startDate);
  const endDate = new Date(challenge.endDate);
  const isUpcoming = startDate > now;
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();
  const progress = isUpcoming ? 0 : Math.min(1, Math.max(0, elapsed / totalDuration));
  const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / 86_400_000));
  const startsIn = Math.max(0, Math.ceil((startDate.getTime() - now.getTime()) / 86_400_000));
  const myStanding = standings.find((s) => s.inviterId === myInviterId);
  const leader = standings[0];
  const counts = challengeCounts(challenge.metric);
  const metricUnit = CHALLENGE_UNIT[challenge.metric] ?? '';
  const MetricIcon = METRIC_ICON[challenge.metric] ?? Trophy;
  const showPrize = !!challenge.prizeAmount && prizeAppliesToTeam(challenge.targetTeam, myTeamCode);
  const targetTeamName = challenge.targetTeam
    ? teamLabel(challenge.targetTeam, challenge.targetTeam === myTeamCode ? myTeamName : null)
    : null;

  const formatValue = (v: number) =>
    challenge.metric === 'HIGHEST_CONVERSION_RATE' ? `${v}%` : String(v);

  return (
    <Card className="group relative overflow-hidden border-brand/20 transition-all hover:border-brand/40 hover:shadow-lg hover:shadow-brand/5">
      {/* Decorative background shapes */}
      <div className="pointer-events-none absolute -right-8 -top-8 h-36 w-36 rounded-full bg-gradient-to-bl from-brand/[0.08] to-transparent" />
      <div className="pointer-events-none absolute -bottom-6 -left-6 h-28 w-28 rounded-full bg-gradient-to-tr from-brand/[0.04] to-transparent" />
      {!isUpcoming && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted/30">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand/60 to-brand transition-all"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}

      <CardContent className="relative space-y-4 p-5">
        {/* Header: icon + title + status */}
        <div className="flex items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isUpcoming ? 'bg-muted/60' : 'bg-brand/10'}`}>
            <MetricIcon className={`h-5 w-5 ${isUpcoming ? 'text-muted-foreground' : 'text-primary'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-bold leading-tight">{challenge.name}</h3>
              {isUpcoming ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                  <Clock className="h-3 w-3" /> Starts in {startsIn}d
                </span>
              ) : (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand/15 px-2.5 py-1 text-xs font-bold tabular-nums text-brand">
                  <Zap className="h-3 w-3" /> {daysLeft}d left
                </span>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <CalendarDays className="h-3 w-3" />
              {challengeDates(challenge.startDate, challenge.endDate)}
            </p>
            {counts && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">What counts:</span> {counts}
              </p>
            )}
            {challenge.description && (
              <p className="mt-1 text-sm text-muted-foreground">{challenge.description}</p>
            )}
          </div>
        </div>

        {/* Your stats: rank + metric value */}
        {!isUpcoming && (
          <div className="rounded-xl border border-border/40 bg-muted/15 p-3">
            {myStanding ? (
              <div className="flex items-center gap-4">
                {/* Rank */}
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Rank
                  </span>
                  <span className="text-3xl font-black tabular-nums leading-none text-primary">
                    #{myStanding.rank}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    of {standings.length}
                  </span>
                </div>
                {/* Divider */}
                <div className="h-12 w-px bg-border/50" />
                {/* Stat */}
                <div className="flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-black tabular-nums leading-none">
                      {formatValue(myStanding.value)}
                    </span>
                    {metricUnit && (
                      <span className="text-xs font-medium text-muted-foreground">{metricUnit}</span>
                    )}
                  </div>
                  {leader && leader.inviterId !== myInviterId && myStanding.value < leader.value && (
                    <p className="mt-1 text-[11px] text-muted-foreground/70">
                      {formatValue(leader.value - myStanding.value)} behind 1st place
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 py-1">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/40">
                  <Sparkles className="h-5 w-5 text-muted-foreground/50" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Not ranked yet</p>
                  <p className="text-xs text-muted-foreground">
                    You'll appear here once you have something that counts.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {isUpcoming && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-border/30 bg-muted/15 py-4 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            Starts {formatDate(challenge.startDate)}
          </div>
        )}

        {/* Prize banner: only for challenges open to this outreacher's team */}
        {showPrize && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-500/20 bg-gradient-to-r from-amber-500/[0.1] to-amber-500/[0.03] px-4 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
              <Trophy className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <span className="text-lg font-black text-amber-300">${formatMoney(challenge.prizeAmount)}</span>
              <p className="text-[11px] font-medium text-amber-400/60">
                {challenge.prizeDescription || 'Prize for first place'}
              </p>
            </div>
          </div>
        )}

        {/* Tags */}
        {(targetTeamName || challenge.targetNiche) && (
          <div className="flex flex-wrap items-center gap-2">
            {targetTeamName && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                <Users className="h-3 w-3" />
                {targetTeamName} team only
              </span>
            )}
            {challenge.targetNiche && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                <Target className="h-3 w-3" />
                {nicheName(challenge.targetNiche)} niche only
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
